import { useParams } from 'react-router-dom';
import { queryOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import type { UseQueryRefetchFn, UseQueryStatus } from '../../@types/types';
import type { EndorsementData } from '../containers/ContractBuilder/types';
import { getRequestAuthPayload } from '../helpers/getRequestAuth';

import type { QueryFactory } from './factory.types';

const getUrl = (submissionId: string) => [`submissions`, submissionId, `endorsements`].join('/');

export const fetchEndorsements = async (submissionId: string): Promise<EndorsementData[]> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: getUrl(submissionId),
  });

  const response = await axios<EndorsementData[]>(networkPayload);
  return response.data;
};

export const endorsementQueries = {
  forSubmission: (submissionId: string) =>
    queryOptions({
      queryKey: ['endorsements', submissionId] as const,
      queryFn: () => fetchEndorsements(submissionId),
      staleTime: 0,
    }),
} satisfies QueryFactory;

interface EndorsementDataHook {
  data: EndorsementData[] | undefined;
  status: UseQueryStatus;
  isFetching: boolean;
  refetch: UseQueryRefetchFn<any, any>;
}

export const useEndorsementData = (): EndorsementDataHook => {
  const { id = '' } = useParams<{ id: string }>();
  const { data, status, isFetching, refetch } = useQuery(endorsementQueries.forSubmission(id));
  return { data: data as EndorsementData[], status, isFetching, refetch };
};
